const API_BASE = '/api';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

// Safely parse a fetch response body as JSON. If the body isn't JSON
// (e.g. CRA dev proxy returned "Proxy error: ..." plain text, or nginx
// returned an HTML error page), read it as text and throw a readable
// error instead of letting JSON.parse blow up.
export const parseJsonResponse = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    const text = await response.text();
    throw new Error(response.ok ? 'Unexpected response format' : (text.slice(0, 120) || 'Request failed'));
  }
  return response.json();
};

// Generic fetch wrapper
const fetchApi = async (endpoint, options = {}) => {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers
    }
  });

  const data = await parseJsonResponse(response);

  if (!response.ok) {
    throw new Error(data.error || 'API request failed');
  }

  return data;
};

// Auth API
export const authApi = {
  register: (userData) => fetchApi('/auth/register', {
    method: 'POST',
    body: JSON.stringify(userData)
  }),
  
  login: (credentials) => fetchApi('/auth/login', {
    method: 'POST',
    body: JSON.stringify(credentials)
  }),
  
  getMe: () => fetchApi('/auth/me'),
  
  updateProfile: (data) => fetchApi('/auth/me', {
    method: 'PUT',
    body: JSON.stringify(data)
  })
};

// Contacts API
export const contactsApi = {
  getAll: () => fetchApi('/contacts'),
  
  getOne: (id) => fetchApi(`/contacts/${id}`),
  
  create: (contactData) => fetchApi('/contacts', {
    method: 'POST',
    body: JSON.stringify(contactData)
  }),
  
  update: (id, contactData) => fetchApi(`/contacts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(contactData)
  }),
  
  delete: (id) => fetchApi(`/contacts/${id}`, {
    method: 'DELETE'
  }),
  
  getInsights: (id) => fetchApi(`/contacts/${id}/insights`)
};

// Chats API
export const chatsApi = {
  getAll: () => fetchApi('/chats'),
  
  getOne: (id) => fetchApi(`/chats/${id}`),
  
  create: (chatData) => fetchApi('/chats', {
    method: 'POST',
    body: JSON.stringify(chatData)
  }),
  
  update: (id, chatData) => fetchApi(`/chats/${id}`, {
    method: 'PUT',
    body: JSON.stringify(chatData)
  }),
  
  delete: (id) => fetchApi(`/chats/${id}`, {
    method: 'DELETE'
  }),
  
  getMessages: (chatId) => fetchApi(`/chats/${chatId}/messages`),
  
  addMessage: (chatId, messageData) => fetchApi(`/chats/${chatId}/messages`, {
    method: 'POST',
    body: JSON.stringify(messageData)
  }),
  
  addMessagesBulk: (chatId, messages) => fetchApi(`/chats/${chatId}/messages/bulk`, {
    method: 'POST',
    body: JSON.stringify({ messages })
  }),

  getMessageAnatomy: (chatId, messageId) => fetchApi(`/chats/${chatId}/messages/${messageId}/anatomy`)
};

// Teams API
export const teamsApi = {
  getAll: () => fetchApi('/teams'),
  
  getOne: (id) => fetchApi(`/teams/${id}`),
  
  create: (teamData) => fetchApi('/teams', {
    method: 'POST',
    body: JSON.stringify(teamData)
  }),
  
  update: (id, teamData) => fetchApi(`/teams/${id}`, {
    method: 'PUT',
    body: JSON.stringify(teamData)
  }),
  
  delete: (id) => fetchApi(`/teams/${id}`, {
    method: 'DELETE'
  }),
  
  addMember: (teamId, contactId, role = null) => fetchApi(`/teams/${teamId}/members`, {
    method: 'POST',
    body: JSON.stringify({ contact_id: contactId, role })
  }),
  
  removeMember: (teamId, contactId) => fetchApi(`/teams/${teamId}/members/${contactId}`, {
    method: 'DELETE'
  })
};

// Life Manager API (goals, commitments, patterns, state)
export const lifeApi = {
  // Full context for AI
  getContext: () => fetchApi('/life/context'),

  // Goals
  getGoals: (status) => fetchApi(`/life/goals${status ? `?status=${status}` : ''}`),
  createGoal: (data) => fetchApi('/life/goals', { method: 'POST', body: JSON.stringify(data) }),
  updateGoal: (id, data) => fetchApi(`/life/goals/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteGoal: (id) => fetchApi(`/life/goals/${id}`, { method: 'DELETE' }),
  checkinGoal: (id, data) => fetchApi(`/life/goals/${id}/checkin`, { method: 'POST', body: JSON.stringify(data) }),

  // Commitments
  getCommitments: () => fetchApi('/life/commitments'),
  createCommitment: (data) => fetchApi('/life/commitments', { method: 'POST', body: JSON.stringify(data) }),
  updateCommitment: (id, data) => fetchApi(`/life/commitments/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCommitment: (id) => fetchApi(`/life/commitments/${id}`, { method: 'DELETE' }),

  // Patterns
  getPatterns: () => fetchApi('/life/patterns'),

  // States
  getStates: (limit) => fetchApi(`/life/states${limit ? `?limit=${limit}` : ''}`),

  // Motivation
  getMotivation: () => fetchApi('/life/motivation'),

  // Bulk AI update (called after each AI response)
  aiUpdate: (data) => fetchApi('/life/ai-update', { method: 'POST', body: JSON.stringify(data) }),
};

// Notifications API
export const notificationsApi = {
  getVapidKey: () => fetchApi('/notifications/vapid-key'),
  subscribe: (subscription) => fetchApi('/notifications/subscribe', { method: 'POST', body: JSON.stringify({ subscription }) }),
  unsubscribe: (endpoint) => fetchApi('/notifications/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint }) }),
  getPreferences: () => fetchApi('/notifications/preferences'),
  updatePreferences: (prefs) => fetchApi('/notifications/preferences', { method: 'PUT', body: JSON.stringify(prefs) }),
};

// Dashboard API (admin only)
export const dashboardApi = {
  getSummary: () => fetchApi('/dashboard/summary'),
  getCategories: () => fetchApi('/dashboard/categories'),
  getScores: () => fetchApi('/dashboard/scores'),
  getPTM: () => fetchApi('/dashboard/ptm'),
  getSentiment: () => fetchApi('/dashboard/sentiment'),
  updateAction: (id, data) => fetchApi(`/dashboard/ptm-actions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  refresh: () => fetchApi('/dashboard/refresh', { method: 'POST' }),
};

// Psychometric API
export const psychometricApi = {
  getQuestions: () => fetchApi('/psychometric/questions'),
  submit: (responses) => fetchApi('/psychometric/submit', {
    method: 'POST',
    body: JSON.stringify({ responses })
  }),
  getProfile: () => fetchApi('/psychometric/profile'),
  getStatus: () => fetchApi('/psychometric/status'),
};

// Persona API
export const personaApi = {
  get: () => fetchApi('/persona'),
  update: (data) => fetchApi('/persona', {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
};

// Numerology API (admin only)
export const numerologyApi = {
  calculate: (dob) => fetchApi('/numerology/calculate', {
    method: 'POST',
    body: JSON.stringify({ dob })
  }),
  calculateBatch: (entries) => fetchApi('/numerology/calculate-batch', {
    method: 'POST',
    body: JSON.stringify({ entries })
  }),
};

// Certification API (admin only for evaluate/latest/history, public for the rest)
export const certificationApi = {
  evaluate: (orgName) => fetchApi('/certification/evaluate', {
    method: 'POST',
    body: JSON.stringify({ org_name: orgName })
  }),
  getLatest: () => fetchApi('/certification/latest'),
  getHistory: () => fetchApi('/certification/history'),
};
